import { costCenterMatches } from '@loot/shared';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Branch } from '../../api.ts';
import {
  ListToolbar,
  matchesStatus,
  SearchBox,
  StatusFilter,
  type StatusFilterValue,
} from '../../components/ListFilters.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { Badge, Button, EmptyRow, Select, Table } from '../../components/ui.tsx';
import { locationUnitName, type Place, type Unit } from '../facilities/location-form.tsx';
import { parentLabel, type CostCenter } from '../governance/cost-center-form.tsx';
import { Fact, Facts } from '../people/common.tsx';
import { useFormat } from '../purchasing/common.tsx';

/** Abas de consulta da ficha da Empresa. Criar e alterar abre o formulário na própria janela. */

function ActiveBadge({ active }: { active: boolean }) {
  const { t } = useTranslation();
  return active ? (
    <Badge tone="ok">{t('fields.active')}</Badge>
  ) : (
    <Badge tone="off">{t('fields.inactive')}</Badge>
  );
}

const has = (text: string | null | undefined, q: string) => (text ?? '').toLowerCase().includes(q);

export function BranchesTab({
  branches,
  canEdit,
  onNew,
  onEdit,
}: {
  branches: Branch[];
  canEdit: boolean;
  onNew: () => void;
  onEdit: (b: Branch) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilterValue>('active');
  const s = q.trim().toLowerCase();
  const rows = branches.filter(
    (b) =>
      matchesStatus(b.isActive, status) &&
      (!s || has(b.code, s) || has(b.name, s) || has(b.taxId, s)),
  );
  return (
    <>
      <ListToolbar
        action={
          canEdit && (
            <Button variant="secondary" onClick={onNew}>
              {t('branches.new')}
            </Button>
          )
        }
      >
        <SearchBox value={q} onChange={setQ} placeholder={t('legalEntities.record.searchBranch')} />
        <StatusFilter value={status} onChange={setStatus} />
      </ListToolbar>
      <p className="text-xs text-muted">{t('legalEntities.record.branchesHint')}</p>
      <Table
        head={[
          t('fields.code'),
          t('fields.name'),
          t('branches.taxId'),
          t('branches.stateRegistration'),
          t('branches.address.city'),
          t('fields.status'),
          '',
        ]}
      >
        {rows.length === 0 && <EmptyRow colSpan={7}>{t('branches.empty')}</EmptyRow>}
        {rows.map((b) => (
          <tr key={b.id}>
            <td className="font-mono">{b.code}</td>
            <td>
              {b.name}
              <div className="text-xs text-muted">{t(`branches.kinds.${b.kind}`)}</div>
            </td>
            <td className="font-mono text-xs">{b.taxId ?? '—'}</td>
            <td className="font-mono text-xs">{b.stateRegistration ?? '—'}</td>
            <td className="text-xs">
              {[b.address.city, b.address.region].filter(Boolean).join(' · ') || '—'}
            </td>
            <td>
              <ActiveBadge active={b.isActive} />
            </td>
            <td className="text-right">
              <span className="flex justify-end gap-2">
                {canEdit && (
                  <Button variant="secondary" onClick={() => onEdit(b)}>
                    {t('common.edit')}
                  </Button>
                )}
                <MasterDeleteButton table="core.branch" id={b.id} compact />
              </span>
            </td>
          </tr>
        ))}
      </Table>
    </>
  );
}

export function LocationsTab({
  units,
  locations,
  canEdit,
  onNew,
  onEdit,
}: {
  units: Unit[];
  locations: Place[];
  canEdit: boolean;
  onNew: () => void;
  onEdit: (l: Place) => void;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [branchId, setBranchId] = useState('');
  const [status, setStatus] = useState<StatusFilterValue>('active');
  const s = q.trim().toLowerCase();
  const rows = locations.filter(
    (l) =>
      (!branchId || l.branchId === branchId) &&
      matchesStatus(l.isActive, status) &&
      (!s || has(l.path, s) || has(l.code, s)),
  );
  return (
    <>
      <ListToolbar
        action={
          canEdit &&
          units.length > 0 && (
            <Button variant="secondary" onClick={onNew}>
              {t('facilities.locations.new')}
            </Button>
          )
        }
      >
        <SearchBox
          value={q}
          onChange={setQ}
          placeholder={t('legalEntities.record.searchLocation')}
        />
        <Select
          className="w-full sm:w-64"
          aria-label={t('facilities.fields.branch')}
          value={branchId}
          onChange={(e) => setBranchId(e.target.value)}
        >
          <option value="">{t('facilities.locations.allBranches')}</option>
          {units.map((b) => (
            <option key={b.id} value={b.id}>
              {locationUnitName(units, b.id)}
            </option>
          ))}
        </Select>
        <StatusFilter value={status} onChange={setStatus} />
      </ListToolbar>
      {units.length === 0 && (
        <p className="text-sm text-muted">{t('legalEntities.record.locationsNeedBranch')}</p>
      )}
      <Table
        head={[
          t('facilities.locations.path'),
          t('facilities.locations.code'),
          t('facilities.locations.kind'),
          t('facilities.fields.branch'),
          t('fields.status'),
          '',
        ]}
      >
        {rows.length === 0 && <EmptyRow colSpan={6}>{t('facilities.locations.empty')}</EmptyRow>}
        {rows.map((l) => (
          <tr key={l.id}>
            <td>{l.path}</td>
            <td className="font-mono text-xs">{l.code}</td>
            <td className="text-xs">{t(`facilities.locationKinds.${l.kind}`)}</td>
            <td className="font-mono text-xs">
              {units.find((u) => u.id === l.branchId)?.code ?? '—'}
            </td>
            <td>
              <ActiveBadge active={l.isActive} />
            </td>
            <td className="text-right">
              <span className="flex justify-end gap-2">
                {canEdit && (
                  <Button variant="secondary" onClick={() => onEdit(l)}>
                    {t('common.edit')}
                  </Button>
                )}
                <MasterDeleteButton table="core.location" id={l.id} compact />
              </span>
            </td>
          </tr>
        ))}
      </Table>
    </>
  );
}

export function CostCentersTab({
  list,
  canCreate,
  canEdit,
  onNew,
  onEdit,
}: {
  list: CostCenter[];
  canCreate: boolean;
  /** Alterar depende também do servidor, linha a linha (`canEdit` de cada centro). */
  canEdit: boolean;
  onNew: () => void;
  onEdit: (c: CostCenter) => void;
}) {
  const fmt = useFormat();
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilterValue>('active');
  const s = q.trim().toLowerCase();
  const rows = list.filter(
    (c) =>
      matchesStatus(c.isActive, status) &&
      (costCenterMatches(q, c.code, c.name) || has(c.netsuiteCode, s)),
  );
  return (
    <>
      <ListToolbar
        action={
          canCreate && (
            <Button variant="secondary" onClick={onNew}>
              {t('governance.costCenters.add')}
            </Button>
          )
        }
      >
        <SearchBox value={q} onChange={setQ} placeholder={t('structure.searchPlaceholder')} />
        <StatusFilter value={status} onChange={setStatus} />
      </ListToolbar>
      <Table
        head={[
          t('governance.fields.code'),
          t('governance.fields.name'),
          t('governance.costCenters.responsibles.master'),
          t('governance.costCenters.approval.approver'),
          t('governance.fields.validity'),
          t('governance.fields.situation'),
          '',
        ]}
      >
        {rows.length === 0 && <EmptyRow colSpan={7}>{t('governance.costCenters.empty')}</EmptyRow>}
        {rows.map((c) => (
          <tr key={c.id}>
            <td className="font-mono text-xs">
              {c.code}
              {c.parentId && <div className="text-muted">↑ {parentLabel(c, list)}</div>}
            </td>
            <td>
              {c.name}
              {c.netsuiteCode && (
                <div className="text-xs text-muted">NetSuite: {c.netsuiteCode}</div>
              )}
            </td>
            <td className="text-xs">
              {c.masterResponsibleName ?? <span className="text-muted">—</span>}
            </td>
            <td className="text-xs">
              {c.managerName ?? (
                <span className="text-muted">{t('governance.costCenters.approval.none')}</span>
              )}
            </td>
            <td className="text-xs whitespace-nowrap">
              {fmt.date(c.validFrom)} → {c.validUntil ? fmt.date(c.validUntil) : '…'}
            </td>
            <td>
              <Badge tone={c.isActive ? 'ok' : 'neutral'}>
                {t(c.isActive ? 'governance.active' : 'governance.inactive')}
              </Badge>
            </td>
            <td className="text-right">
              <span className="flex justify-end gap-2">
                {canEdit && c.canEdit && (
                  <Button variant="secondary" onClick={() => onEdit(c)}>
                    {t('common.edit')}
                  </Button>
                )}
                <MasterDeleteButton table="core.cost_center" id={c.id} compact />
              </span>
            </td>
          </tr>
        ))}
      </Table>
      <p className="text-xs text-muted">{t('legalEntities.record.costCentersHint')}</p>
    </>
  );
}

export interface Subsidiary {
  id: string;
  isElimination: boolean;
  netsuiteCode: string | null;
}

export function IntegrationTab({
  subsidiary,
  parentLabel,
  canEdit,
  onEdit,
}: {
  subsidiary: Subsidiary | undefined;
  parentLabel: string;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <section className="flex flex-col gap-3">
        <h3 className="border-b border-rule pb-1.5 text-xs font-semibold text-muted">
          {t('legalEntities.record.consolidation')}
        </h3>
        <Facts>
          <Fact label={t('structure.fields.netsuiteCode')}>
            <span className="font-mono">{subsidiary?.netsuiteCode}</span>
          </Fact>
          <Fact label={t('structure.fields.elimination')}>
            {subsidiary?.isElimination ? t('common.yes') : t('common.no')}
          </Fact>
          <Fact label={t('legalEntities.parent')}>{parentLabel}</Fact>
        </Facts>
        {canEdit && subsidiary && (
          <div>
            <Button variant="secondary" onClick={onEdit}>
              {t('legalEntities.record.editIntegration')}
            </Button>
          </div>
        )}
      </section>
      <section className="flex flex-col gap-2 rounded-lg bg-paper p-4 text-sm">
        <strong className="font-semibold">{t('legalEntities.record.notPayerTitle')}</strong>
        <p className="text-muted">{t('legalEntities.record.notPayerText')}</p>
      </section>
    </div>
  );
}

export interface RepresentativeRow {
  id: string;
  fullName: string;
  status: string;
  representations: { id: string; legalEntityCode: string; kind: string; status: string }[];
  validSignatory: boolean;
  pending: number;
  expired: number;
}

export function RepresentativesTab({
  rows,
  entityCode,
}: {
  rows: RepresentativeRow[];
  entityCode: string;
}) {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const s = q.trim().toLowerCase();
  const visible = rows.filter((r) => !s || has(r.fullName, s));
  return (
    <>
      <ListToolbar
        action={
          <Link to="/representatives" className="text-sm font-medium text-brand-text">
            {t('legalEntities.record.manageRepresentatives')}
          </Link>
        }
      >
        <SearchBox value={q} onChange={setQ} placeholder={t('representatives.search')} />
      </ListToolbar>
      <Table
        head={[
          t('representatives.fullName'),
          t('representatives.kind'),
          t('representatives.validSignatory'),
          t('fields.status'),
          '',
        ]}
      >
        {visible.length === 0 && (
          <EmptyRow colSpan={5}>{t('legalEntities.record.noRepresentatives')}</EmptyRow>
        )}
        {visible.map((r) => (
          <tr key={r.id}>
            <td>{r.fullName}</td>
            <td className="text-xs">
              {r.representations
                .filter((x) => x.legalEntityCode === entityCode)
                .map((x) => t(`representatives.kinds.${x.kind}`))
                .join(', ') || '—'}
            </td>
            <td>
              {r.validSignatory ? (
                <Badge tone="ok">{t('common.yes')}</Badge>
              ) : (
                <Badge tone="neutral">{t('common.no')}</Badge>
              )}
              {r.pending > 0 && (
                <div className="mt-1">
                  <Badge tone="info">{t('representatives.pendingCount', { n: r.pending })}</Badge>
                </div>
              )}
            </td>
            <td>
              <Badge tone={r.status === 'active' ? 'ok' : 'off'}>
                {t(`representatives.statuses.${r.status}`)}
              </Badge>
            </td>
            <td className="text-right">
              <span className="flex items-center justify-end gap-2">
                <Link
                  to="/representatives/$id"
                  params={{ id: r.id }}
                  className="text-sm font-medium text-brand-text"
                >
                  {t('legalEntities.record.openRepresentative')}
                </Link>
                <MasterDeleteButton table="core.representative" id={r.id} compact />
              </span>
            </td>
          </tr>
        ))}
      </Table>
      <p className="text-xs text-muted">{t('legalEntities.record.representativesHint')}</p>
    </>
  );
}
